import React, { useState, useEffect } from 'react';
import Navbar  from '../components/layout/Navbar';
import Footer  from '../components/layout/Footer';
import { completedProjectService } from '../api/services';
import { Play, Calendar, Tag, Loader2, AlertCircle, X } from 'lucide-react';

// ── Helpers ───────────────────────────────────────────────────

// Extract YouTube video ID from any YouTube URL format
const getYoutubeId = (url) => {
  if (!url) return null;
  const patterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([A-Za-z0-9_-]{11})/,
    /youtube\.com\/shorts\/([A-Za-z0-9_-]{11})/,
  ];
  for (const p of patterns) {
    const m = url.match(p);
    if (m) return m[1];
  }
  return null;
};

const getYoutubeThumbnail = (url) =>
  `https://img.youtube.com/vi/${getYoutubeId(url)}/hqdefault.jpg`;

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-GB', { month: 'short', year: 'numeric' }) : null;

// ── Video Modal ───────────────────────────────────────────────
const VideoModal = ({ project, onClose }) => {
  const isYoutube = project.videoSource === 'YOUTUBE';
  const ytId      = isYoutube ? getYoutubeId(project.youtubeUrl) : null;

  // Close on Escape
  useEffect(() => {
    const handler = (e) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        className="relative w-full max-w-4xl bg-black rounded-2xl overflow-hidden shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Close button */}
        <button
          onClick={onClose}
          className="absolute top-3 right-3 z-10 w-9 h-9 bg-black/60 hover:bg-black/90 text-white rounded-full flex items-center justify-center transition-colors"
        >
          <X size={18} />
        </button>

        {/* Player */}
        <div className="aspect-video w-full">
          {isYoutube && ytId ? (
            <iframe
              src={`https://www.youtube.com/embed/${ytId}?autoplay=1&rel=0`}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              title={project.title}
            />
          ) : (
            <video
              src={project.videoUrl}
              controls
              autoPlay
              className="w-full h-full object-contain bg-black"
            />
          )}
        </div>

        {/* Info strip */}
        <div className="bg-forest-900 text-cream-50 px-6 py-4">
          <h3 className="font-serif font-semibold text-lg">{project.title}</h3>
          {project.clientName && (
            <p className="text-cream-300 text-sm mt-0.5">{project.clientName}</p>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Project Card ──────────────────────────────────────────────
const ProjectCard = ({ project, onClick }) => {
  const isYoutube = project.videoSource === 'YOUTUBE';
  const thumb     = project.thumbnailUrl ||
    (isYoutube ? getYoutubeThumbnail(project.youtubeUrl) : null);

  const tags = project.tags ? (
    typeof project.tags === 'string' ? JSON.parse(project.tags) : project.tags
  ) : [];

  return (
    <div
      onClick={onClick}
      className="group bg-white rounded-2xl overflow-hidden shadow-sm border border-cream-200 hover:shadow-lg hover:-translate-y-1 transition-all duration-300 cursor-pointer"
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-forest-900 overflow-hidden">
        {thumb ? (
          <img
            src={thumb}
            alt={project.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-forest-800 to-forest-900">
            <Play size={40} className="text-cream-300/60" />
          </div>
        )}

        {/* Play overlay */}
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
          <div className="w-14 h-14 bg-white/90 rounded-full flex items-center justify-center shadow-lg">
            <Play size={22} className="text-forest-900 ml-1" fill="currentColor" />
          </div>
        </div>

        {/* Source badge */}
        <div className="absolute top-3 left-3">
          {isYoutube ? (
            <span className="bg-red-600 text-white text-[10px] font-bold px-2 py-1 rounded-md tracking-wide">
              YouTube
            </span>
          ) : (
            <span className="bg-forest-700 text-cream-50 text-[10px] font-bold px-2 py-1 rounded-md tracking-wide">
              Video
            </span>
          )}
        </div>
      </div>

      {/* Info */}
      <div className="p-5">
        <h3 className="font-serif font-semibold text-forest-900 text-base leading-snug group-hover:text-forest-700 transition-colors line-clamp-2">
          {project.title}
        </h3>

        {project.clientName && (
          <p className="text-sm text-rust-500 font-medium mt-1">{project.clientName}</p>
        )}

        {project.description && (
          <p className="text-sm text-forest-600/80 mt-2 line-clamp-2 leading-relaxed">
            {project.description}
          </p>
        )}

        <div className="flex items-center flex-wrap gap-3 mt-3">
          {formatDate(project.completedAt) && (
            <span className="flex items-center gap-1.5 text-xs text-forest-500">
              <Calendar size={12} /> {formatDate(project.completedAt)}
            </span>
          )}
          {tags.length > 0 && (
            <div className="flex items-center gap-1.5 flex-wrap">
              {tags.slice(0, 3).map((t) => (
                <span key={t} className="text-[11px] bg-cream-100 text-forest-600 px-2 py-0.5 rounded-full border border-cream-200">
                  {t}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────
const CompletedProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await completedProjectService.getAll();
        setProjects(res.data?.data?.projects || res.data?.projects || []);
      } catch (e) {
        setError('Failed to load projects. Please try again later.');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <>
      <Navbar />

      {/* Hero */}
      <div className="pt-28 pb-16 bg-gradient-to-br from-forest-900 via-forest-800 to-forest-900 text-cream-50 text-center px-6">
        <p className="label-tag text-green-400 mb-3">Our Work</p>
        <h1 className="font-serif text-4xl lg:text-5xl font-semibold max-w-2xl mx-auto leading-tight">
          Completed Projects
        </h1>
        <p className="mt-4 text-cream-300 max-w-xl mx-auto text-base leading-relaxed">
          Watch real results from entrepreneurs we've worked with — from business launches to scale-up transformations.
        </p>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16">

        {loading && (
          <div className="flex flex-col items-center justify-center py-24 gap-4">
            <Loader2 size={36} className="text-forest-600 animate-spin" />
            <p className="text-forest-500 text-sm">Loading projects…</p>
          </div>
        )}

        {error && (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-center">
            <AlertCircle size={36} className="text-rust-400" />
            <p className="text-forest-700 font-medium">{error}</p>
          </div>
        )}

        {!loading && !error && projects.length === 0 && (
          <div className="flex flex-col items-center justify-center py-24 gap-3 text-center">
            <Play size={40} className="text-forest-300" />
            <p className="text-forest-500 text-sm">No completed projects yet. Check back soon!</p>
          </div>
        )}

        {!loading && !error && projects.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {projects.map((p) => (
              <ProjectCard key={p.id} project={p} onClick={() => setSelected(p)} />
            ))}
          </div>
        )}
      </div>

      <Footer />

      {/* Modal */}
      {selected && (
        <VideoModal project={selected} onClose={() => setSelected(null)} />
      )}
    </>
  );
};

export default CompletedProjectsPage;